import { useMemo, ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';
import pluralize from 'pluralize';

import type { User } from '@/modules/user/userTypes';
import { Popover } from '@/components/atoms/Popover';
import { sendAnalytics } from '@/services/analyticsService';

import {
	NewFeatureIcon,
	NewFeatureIconWhite,
	NotificationsEmailIcon,
	NotificationsEmailIconWhite,
	NotificationsOpensIcon,
	NotificationsOpensIconWhite
} from '@/resources/assets/images';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { useNotificationMutation } from '../hooks/useNotificationMutation';
import { useNotificationQuery } from '../hooks/useNotificationQuery';

type NotificationModalProps = {
	user: User;
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
};

export const NotificationModal = ({
	user,
	isOpen,
	onClose,
	children
}: NotificationModalProps) => {
	const ref = useRef<HTMLDivElement>(null);

	useOnClickOutside({
		ref,
		handler: onClose,
		enabled: isOpen
	});

	const { userNotifications } = useNotificationQuery({
		user
	});
	const { mutate } = useNotificationMutation();

	const handleUpdateUserAnalytics = (message: string) => {
		sendAnalytics({
			type: 'track',
			eventName: 'User clicked notification',
			properties: {
				type: message
			}
		});
	};

	const handlePostNotificationRead = () => {
		mutate();
	};

	const isEmpty =
		!userNotifications ||
		(userNotifications?.replies === 0 &&
			userNotifications?.opens === 0 &&
			userNotifications?.features?.length === 0 &&
			userNotifications?.featuresAll?.length === 0);

	const newNotifications = useMemo(
		() =>
			typeof userNotifications === 'object' &&
			Object.keys(userNotifications)?.length > 0 &&
			(userNotifications?.replies > 0 ||
				userNotifications?.opens > 0 ||
				userNotifications?.features?.length > 0),
		[userNotifications]
	);

	const newFeaturesKey = useMemo(() => {
		if (!newNotifications) return null;

		return userNotifications?.features.reduce((acc, curr) => {
			acc[curr.id] = true;

			return acc;
		}, {});
	}, [newNotifications, userNotifications?.features]);

	return (
		<div ref={ref}>
			<Popover.Root open={isOpen}>
				<Popover.Anchor>{children}</Popover.Anchor>

				<Popover.Content className="bg-dm-black1 p-0" align="start">
					<header className="flex items-center justify-between border-b border-gray-700 p-4">
						<h2 className="text-sm font-semibold text-dm-white">
							Notifications
						</h2>
					</header>

					<div className="w-full divide-y divide-gray-700">
						{isEmpty ? (
							<p className="p-4 text-center text-sm">
								You currently have no new notifications
							</p>
						) : (
							<>
								{userNotifications?.opens > 0 && (
									<button
										type="button"
										className="w-full"
										onClick={() => {
											onClose();
											handlePostNotificationRead();

											handleUpdateUserAnalytics(
												'New unchecked opened emails'
											);
										}}
									>
										<Link
											data-is-notification-read={
												!newNotifications
											}
											className="relative inline-flex w-full cursor-pointer gap-3 border-b border-dm-black p-3 hover:bg-dm-black1"
											to="/communication-center?opens=yes"
										>
											<>
												{newNotifications ? (
													<NotificationsOpensIcon />
												) : (
													<NotificationsOpensIconWhite />
												)}
												<p className="w-full text-start text-sm font-semibold">
													You have{' '}
													{userNotifications.opens}{' '}
													new unchecked opened{' '}
													{pluralize(
														'email',
														userNotifications.opens
													)}
													.
												</p>
											</>
										</Link>
									</button>
								)}

								{userNotifications?.replies > 0 && (
									<button
										type="button"
										className="w-full"
										onClick={() => {
											onClose();
											handlePostNotificationRead();

											handleUpdateUserAnalytics(
												'New unchecked replies'
											);
										}}
									>
										<Link
											data-is-notification-read={
												!newNotifications
											}
											className="relative inline-flex w-full cursor-pointer gap-3 border-b border-dm-black p-3 hover:bg-dm-black1"
											to="/communication-center?replies=yes"
										>
											<>
												{newNotifications ? (
													<NotificationsEmailIcon />
												) : (
													<NotificationsEmailIconWhite />
												)}

												<p className="w-full text-start text-sm font-semibold">
													You have{' '}
													{userNotifications.replies}{' '}
													new unchecked{' '}
													{pluralize(
														'reply',
														userNotifications.replies
													)}
													.
												</p>
											</>
										</Link>
									</button>
								)}

								{userNotifications?.features?.length > 0 && (
									<>
										{userNotifications.features.map(
											notification => (
												<button
													type="button"
													className="w-full"
													key={notification.feature}
													onClick={() => {
														onClose();
														handlePostNotificationRead();

														handleUpdateUserAnalytics(
															`${notification.feature} is now available`
														);
													}}
												>
													<span
														data-is-notification-read={
															!newNotifications
														}
														className="relative inline-flex w-full cursor-pointer gap-3 border-b border-dm-black p-3 hover:bg-dm-black1"
													>
														{newNotifications ? (
															<NewFeatureIcon />
														) : (
															<NewFeatureIconWhite />
														)}
														<p className="w-full text-start text-sm font-semibold">
															{
																notification.feature
															}{' '}
															is now available
														</p>
													</span>
												</button>
											)
										)}
									</>
								)}

								{userNotifications?.featuresAll?.length > 0 && (
									<>
										{userNotifications.featuresAll.map(
											notification => {
												if (
													newFeaturesKey[
														notification.id
													]
												)
													return null;

												return (
													<button
														type="button"
														className="w-full"
														key={
															notification.feature
														}
														onClick={() => {
															onClose();
															handlePostNotificationRead();

															handleUpdateUserAnalytics(
																`${notification.feature} is now available`
															);
														}}
													>
														<span
															data-is-notification-read={
																!newNotifications
															}
															className="relative inline-flex w-full cursor-pointer gap-3 border-b border-dm-black p-3 hover:bg-dm-black1"
														>
															<NewFeatureIconWhite />
															<p className="w-full text-start text-sm font-semibold">
																{
																	notification.feature
																}{' '}
																is now available
															</p>
														</span>
													</button>
												);
											}
										)}
									</>
								)}
							</>
						)}
					</div>
				</Popover.Content>
			</Popover.Root>
		</div>
	);
};
